"use client";

import React, { useEffect, useRef, useState } from "react";
import styles from "./styles/SchoolFeedWidget.module.scss";
import { Scrollbar } from "react-scrollbars-custom";
import FontAwesome from "../utilities/FontAwesome";
import SchoolFeedList from "./ContentPostDisplay/SchoolFeedList";
import { observer } from "mobx-react";
import { ErrorBoundary } from "../utilities/errors/ErrorBoundary";
import { OrganizationTypesWithGrids } from "../../types/OrganizationTypeValues";
import ImageComponent from "../utilities/ImageComponent";
import { color_lightness } from "../../utils/colorcontrast";
import classNames from "classnames";
import { reactScrollBarsCustomWidth } from "../../utils/SchoolBlocksUtilities";
import FeedHeader from "../blocks/SchoolBlocks/NewsBlock/FeedListHeader";
import { useSchoolFeedBlockAndWidgetContent } from "./hooks/useSchoolFeedBlockAndWidgetContent";
import ModalBackdrop from "../modals/_ModalBackdrop";
import { useStores } from "../../stores/useStores";
import { FetchMoreNewsButton } from "../blocks/TerracedGridTheme/NewsBlock/FetchMoreNewsButton";
import { TranslatedText, useT } from "../utilities/TranslatedText";
import { Box } from "@radix-ui/themes";

export function ContentButtons(props: {
  handleSave: (
    contentItem: ContentItem,
    organization: BaseOrganization,
    post_to_facebook: boolean,
  ) => void;
  style: string;
}) {
  const { sidebarStore } = useStores();
  return (
    <div className={props.style}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          sidebarStore.setSidebar({
            view: "SchoolFeedPost",
            handleSave: props.handleSave,
          });
        }}
      >
        <FontAwesome prefix={"fas"} name={"fa-plus"} />
        Add New Post
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          sidebarStore.setSidebar({ view: "Social" });
        }}
      >
        <FontAwesome prefix={"fas"} name={"fa-cog"} />
        Sources & Settings
      </button>
    </div>
  );
}
const SchoolFeedWidget: React.FC<{
  organization_id: string;
}> = observer(() => {
  const t = useT();
  const { organizationStore, interfaceStore, i18nStore, styleStore } =
    useStores();
  const isOpen = interfaceStore.schoolFeedWidgetOpen;
  const [contentHeight, setContentHeight] = useState(600);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const {
    contentItems,
    isValidating,
    mutate,
    currentView,
    setCurrentView,
    setSize,
    showViewMore,
    total,
    getItemAtIndex,
  } = useSchoolFeedBlockAndWidgetContent({
    organization__path__ancestor_or_eq:
      organizationStore.currentOrganization.path,
    organization__type__in: Array.from(OrganizationTypesWithGrids),
    organization_id: undefined,
  });

  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const headerRightTextClassName = classNames({
    [styles.headerRightText]: true,
    [styles.headerRightTextThemeColor]:
      color_lightness(
        styleStore.appearance.themes[styleStore.appearance.themeID]
          .primaryNavBackground,
      ) < 50,
  });

  const updateWidthAndHeight = () => {
    setWidth(window.innerWidth);
    setHeight(window.innerHeight);
  };

  useEffect(() => {
    window.addEventListener("resize", updateWidthAndHeight);
    return () => window.removeEventListener("resize", updateWidthAndHeight);
  }, []);

  useEffect(() => {
    const primaryNav = document.getElementById("primaryNav");
    const secondaryNav = document.getElementById("sb-secondary-nav");
    if (primaryNav && secondaryNav) {
      const windowHeight = window.innerHeight;
      const headerHeight = primaryNav.clientHeight + secondaryNav.clientHeight;
      if (interfaceStore.zoomValue >= 250) {
        setContentHeight(windowHeight - 55);
      } else {
        setContentHeight(windowHeight - headerHeight - 80);
      }
    }
  }, [width, height]);

  function toggleWidget() {
    interfaceStore.toggleSchoolFeedWidgetOpen();
    mutate[currentView]();
  }

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape" && interfaceStore.schoolFeedWidgetOpen) {
        toggleWidget();
        const schoolFeedMobileNav = document.getElementById(
          "SchoolFeedMobileNav",
        );
        if (schoolFeedMobileNav) {
          schoolFeedMobileNav.focus();
        } else {
          buttonRef.current?.focus();
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const onMouseDown = () => {
    toggleWidget();
  };

  async function handleDelete() {
    await mutate[currentView]();
  }

  const currentViewOptions = [
    { value: "news", label: "News" },
    { value: "events", label: "Events" },
  ];
  const handleCurrentViewChange = (view: string) => {
    setCurrentView(view);
  };

  const fetchContent = () => {
    setSize[currentView]((s) => s + 1);
  };

  return (
    <ErrorBoundary>
      <div
        className={styles.schoolfeed}
        data-testid={"schoolfeed-widget"}
        style={{
          bottom: -contentHeight,
        }}
      >
        <div
          className={styles.content}
          data-testid={"schoolfeed-widget-content"}
          ref={ref}
          style={{
            transform: `translateY(${isOpen ? -contentHeight : 0}px)`,
          }}
        >
          <div className={styles.header}>
            <button
              onClick={() => toggleWidget()}
              aria-label={"Toggle SchoolFeed Widget"}
              tabIndex={0}
            >
              <ImageComponent
                src={require(`../../assets/images/SchoolFeed-logo-white.png`)}
                className={styles.logo}
                aria-label={`SchoolFeed Logo`}
              />
            </button>
            {isOpen ? (
              <div
                className={headerRightTextClassName}
                style={
                  i18nStore.orgDefaultLocale === i18nStore.localeSetByUrl
                    ? { fontSize: "20px" }
                    : { fontSize: "14px" }
                }
              >
                <div className={styles.allLinks}>
                  <div className={styles.schoolFeedLink}>
                    <TranslatedText>News</TranslatedText>
                  </div>
                  <span>|</span>
                  <div className={styles.schoolFeedLink}>
                    <TranslatedText>Events</TranslatedText>
                  </div>
                  <span>|</span>
                  <div className={styles.schoolFeedLink}>
                    <TranslatedText>Files</TranslatedText>
                  </div>
                  <span>|</span>
                  <div className={styles.schoolFeedLink}>
                    <TranslatedText>Media</TranslatedText>
                  </div>
                </div>
              </div>
            ) : (
              <TranslatedText
                as={"div"}
                className={`${styles.headerRightText}`}
                style={
                  i18nStore.orgDefaultLocale === i18nStore.localeSetByUrl
                    ? { fontSize: "20px" }
                    : { fontSize: "16px" }
                }
              >
                SchoolFeed is your center for News and Events
              </TranslatedText>
            )}
            <button
              id={"schoolfeed"}
              ref={buttonRef}
              className={styles.openButton}
              onClick={() => toggleWidget()}
              aria-label={"Toggle SchoolFeed Widget"}
              tabIndex={0}
            >
              <FontAwesome
                className={`${styles.popupArrow}`}
                size={"3x"}
                name={"fa-angle-right"}
                prefix={"fas"}
                style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0)" }}
              />
            </button>
          </div>
          <div
            className={styles.contentContainer}
            style={{
              height: contentHeight,
            }}
          >
            <Scrollbar
              scrollbarWidth={reactScrollBarsCustomWidth}
              disableTrackYWidthCompensation={true}
              trackYProps={{
                style: { width: 6 },
              }}
              scrollerProps={{
                role: "region",
                "aria-label": t("SchoolFeed"),
                tabIndex: 0,
              }}
            >
              {isOpen && (
                <div className={styles.scroller}>
                  <p>SchoolFeed is your center for news and events.</p>
                  <div className={styles.headerContainer}>
                    <FeedHeader
                      currentView={currentView}
                      handleCurrentViewChange={handleCurrentViewChange}
                      currentViewOptions={currentViewOptions}
                      content={contentItems[currentView]}
                      mutate={mutate[currentView]}
                    />
                  </div>
                  <ErrorBoundary>
                    <SchoolFeedList
                      showMedia={true}
                      allowCuration={true}
                      content={contentItems[currentView]}
                      handleDelete={handleDelete}
                      totalItemCount={total[currentView]}
                      getItemAtIndex={getItemAtIndex[currentView]}
                    />
                    <div className={styles.fetchMoreContainer}>
                      {showViewMore[currentView] && (
                        <Box pt={"2"}>
                          <FetchMoreNewsButton
                            onClick={fetchContent}
                            isLoading={isValidating[currentView]}
                            contentType={currentView}
                          />
                        </Box>
                      )}
                    </div>
                  </ErrorBoundary>
                </div>
              )}
            </Scrollbar>
          </div>
        </div>
      </div>
      {isOpen && (
        <ModalBackdrop active={true} onMouseDown={onMouseDown} zIndex={500} />
      )}
    </ErrorBoundary>
  );
});

export default SchoolFeedWidget;
